import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type ResetPasswordParams = {
  organizationId: number | string;
  userId: number | string;
};

type ResetPasswordResponse = {
  success: boolean;
  message: string;
};

/**
 * Hook to reset a user's password in an on-prem organization
 *
 * POST /api/admin/on-prem/organizations/{organization_id}/users/{user_id}/reset-password
 */
export const useResetAdminOrganizationUserPassword = () => {
  const { t } = useTranslation();

  const resetPassword = async ({
    organizationId,
    userId
  }: ResetPasswordParams) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/users/${userId}/reset-password`;
    const response = await Axios.post<ResetPasswordResponse>(url);
    return response.data;
  };

  return useMutation<
    ResetPasswordResponse,
    AxiosError<BaseError>,
    ResetPasswordParams
  >(resetPassword, {
    onSuccess: () => {
      toast.success(t('password-reset-link-sent'));
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-reset-password');
      toast.error(String(errorMessage));
    }
  });
};
